import { useEffect } from 'react';
import { subscribeAppHomeEvents } from '../lib/sseStore';
import { toast } from 'sonner';
import { notifyError, toastIdForMessage } from '../lib/errors';

/**
 * Listens for server-sent `toast` events and feeds them into the single sonner
 * toast surface.
 *
 * Reauth toasts (`oauthUrl` or `code` present) go through `notifyError`: a
 * persistent Reconnect action with stable dedup id `salesforce-reauth-required`.
 * Every other toast is server-authored copy, so it goes to sonner directly with
 * the server's message and level (`notifyError` would replace text matching no
 * error keyword with generic copy). A missing level means `warn`, matching
 * `emitAppHomeToast`. Identical text shares one sonner id so repeats collapse.
 *
 * Mount once at `AuthenticatedLayout` (AppRouter.tsx), so Path B (SSE) toasts
 * render app-wide, including `/tools/*` mini-app routes.
 */
export function SseToastListener() {
  // Per-frame subscription: two toasts in one network chunk must both show.
  useEffect(
    () =>
      subscribeAppHomeEvents({ view: 'active', filter: 'all' }, (event) => {
        if (event.type !== 'toast') return;
        const { message, level = 'warn', oauthUrl, code } = event;
        if (oauthUrl || code) {
          notifyError({ code: code ?? 'SF_REAUTH_REQUIRED', oauthUrl, userMessage: message }, 'sse');
          return;
        }
        const show = level === 'info' ? toast.info : level === 'error' ? toast.error : toast.warning;
        show(message, { id: toastIdForMessage(message) });
      }),
    [],
  );

  // No DOM output — feeds sonner directly.
  return null;
}
